import { useState, useContext, useEffect } from "react";
import axios from "axios";
import { AppContext } from "../../context/AppContext";
import "./assets/css/addblog.css";
import EditBlog from "./EditBlog";

const AddBlog = () => {
  const adminurl = "http://localhost:4000/api";
  const { baseUrl, Backend_folder_path, token } = useContext(AppContext);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    categoryType: "",
    authorName: "",
    metaTitle: "",
    metaKeyword: "",
    metaDescription: "",
    imagePath: "",
    authorImagePath: "",
    blogStatus: true,
  });

  // Image handling state
  const [thumbnail, setThumbnail] = useState(null);
  const [preview, setPreview] = useState(null);
  const [authorImage, setAuthorImage] = useState(null);
  const [authorPreview, setAuthorPreview] = useState(null);

  // UI state
  const [uploadImgMsg, setUploadImgMsg] = useState("");
  const [uploadAuthorImgMsg, setUploadAuthorImgMsg] = useState("");
  const [status, setStatus] = useState("");
  const [isError, setIsError] = useState(false);

  // get data state
  const [blogData, setBlogData] = useState([]);
  const [editBlogId, setEditBlogId] = useState(null);

  // blog delete model state
  const [isDelete, setIsDelete] = useState(false);
  const [deleteBlogId, setDeleteBlogId] = useState(null);

  useEffect(() => {
    handleGetBlog();
  }, []);

  const handleGetBlog = async () => {
    try {
      const response = await axios.get(`${baseUrl}/get-blog`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response) {
        setBlogData(response.data.getAllBlog);
      }
    } catch (error) {
      console.error("Error receiving blog:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (type === "thumbnail") {
      setThumbnail(file);
      setPreview(URL.createObjectURL(file));
    } else if (type === "author") {
      setAuthorImage(file);
      setAuthorPreview(URL.createObjectURL(file));
    }
  };

  const handleThumbUpload = async (e, type) => {
    e.preventDefault();
    const file = type === "thumbnail" ? thumbnail : authorImage;
    const msgSetter =
      type === "thumbnail" ? setUploadImgMsg : setUploadAuthorImgMsg;

    if (!file) {
      msgSetter(`Please select a ${type} image first!`);
      return;
    }

    const formData = new FormData();
    // Match the backend's expected form data key
    formData.append(
      type === "thumbnail" ? "blog_images" : "blog_author_images",
      file
    );

    try {
      const endpoint =
        type === "thumbnail"
          ? `${baseUrl}/upload-blog-images`
          : `${baseUrl}/upload-blog-author-images`;

      const res = await axios.post(endpoint, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      msgSetter(
        `${
          type.charAt(0).toUpperCase() + type.slice(1)
        } image uploaded successfully`
      );
      // Update formData with the image path (based on backend storage)
      setFormData((prev) => ({
        ...prev,
        [type === "thumbnail" ? "imagePath" : "authorImagePath"]:
          type === "thumbnail"
            ? `/blog/blog_images/${file.name}`
            : `/blog/blog_author_images/${file.name}`,
      }));
    } catch (err) {
      console.error(err);
      msgSetter(`Failed to upload ${type} image: ${err.message}`);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      categoryType: "",
      authorName: "",
      metaTitle: "",
      metaKeyword: "",
      metaDescription: "",
      imagePath: "",
      authorImagePath: "",
      blogStatus: true,
      url_key: "",
    });
    setThumbnail(null);
    setAuthorImage(null);
    setPreview(null);
    setAuthorPreview(null);
    setUploadImgMsg("");
    setUploadAuthorImgMsg("");
    setStatus("");
    setIsError(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("");
    setIsError(false);

    if (!formData.title || !formData.categoryType) {
      setStatus("Blog title and tags are required.");
      setIsError(true);
      return;
    }

    if (!formData.imagePath || !formData.authorImagePath) {
      setStatus("Please upload both blog and author images.");
      setIsError(true);
      return;
    }

    try {
      const response = await axios.post(
        `${baseUrl}/save-blog`,
        {
          title: formData.title,
          description: formData.description,
          categoryType: formData.categoryType,
          authorName: formData.authorName,
          metaTitle: formData.metaTitle,
          metaKeyword: formData.metaKeyword,
          metaDescription: formData.metaDescription,
          imagePath: formData.imagePath,
          authorImagePath: formData.authorImagePath,
          blogStatus: formData.blogStatus,
          url_key: formData.url_key,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setStatus("Blog saved successfully!");
        setIsError(false);
        resetForm();
        handleGetBlog(); // refresh blog list
      } else {
        setStatus(response.data.message || "Failed to save blog.");
        setIsError(true);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "An error occurred while saving the blog.";
      setStatus(errorMessage);
      setIsError(true);
    }
  };

  // Delete blog
  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${baseUrl}/delete-blog/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        ("Blog deleted successfully!");
        setIsError(false);
        handleGetBlog(); // refresh blog list
      } else {
        setIsError(true);
      }
    } catch (error) {
      console.error("Error deleting blog:", error);
      setIsError(true);
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (id) => {
    setDeleteBlogId(id);
    setIsDelete(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (deleteBlogId) {
      await handleDelete(deleteBlogId);
      setIsDelete(false);
      setDeleteBlogId(null);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setIsDelete(false);
    setDeleteBlogId(null);
  };

  return (
    <div className="add_blog_container">
      <div className="blog_main">
        <h1 className="heading">Add Blog</h1>
      </div>
      <div className="add_blog_main">
        <div className="blog_main_model">
          <form
            onSubmit={handleSubmit}
            style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
          >
            <div className="form_field">
              <label>
                <input
                  type="checkbox"
                  name="blogStatus"
                  checked={formData.blogStatus}
                  onChange={handleInputChange}
                />
                <span> Enable Blog (Published)</span>
              </label>
            </div>

            <div className="form_field">
              <label>Blog Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="form-control"
                placeholder="Enter blog title"
              />
            </div>
            <div className="form_field">
              <label>Url Key</label>
              <input
                type="text"
                name="url_key"
                value={formData.url_key}
                onChange={handleInputChange}
                required
                className="form-control"
                placeholder="Enter URL key"
              />
            </div>
            <div className="form_field">
              <label>Blog Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
                cols="25"
                placeholder="Enter blog description..."
                className="form-control"
              />
            </div>

            <div className="form_field">
              <label>Blog Category</label>
              <input
                type="text"
                name="categoryType"
                value={formData.categoryType}
                onChange={handleInputChange}
                required
                className="form-control"
                placeholder="Enter blog tags (comma separated)"
              />
            </div>

            <div className="form_description">
              <label>Blog Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, "thumbnail")}
              />
              {preview && (
                <img
                  src={preview}
                  alt="Blog Image Preview"
                  style={{ width: "100px", height: "100px", marginTop: "10px" }}
                />
              )}
              <button
                type="button"
                onClick={(e) => handleThumbUpload(e, "thumbnail")}
                disabled={!thumbnail}
              >
                Upload Blog Image
              </button>
              {uploadImgMsg && (
                <p
                  style={{
                    color: uploadImgMsg.includes("Failed") ? "red" : "green",
                  }}
                >
                  {uploadImgMsg}
                </p>
              )}
            </div>

            <div className="form_field">
              <label>Author Name</label>
              <input
                type="text"
                name="authorName"
                value={formData.authorName}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Enter author name"
              />
            </div>

            <div className="form_description">
              <label>Author Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, "author")}
              />
              {authorPreview && (
                <img
                  src={authorPreview}
                  alt="Author Image Preview"
                  style={{ width: "100px", height: "100px", marginTop: "10px" }}
                />
              )}
              <button
                type="button"
                onClick={(e) => handleThumbUpload(e, "author")}
                disabled={!authorImage}
              >
                Upload Author Image
              </button>
              {uploadAuthorImgMsg && (
                <p
                  style={{
                    color: uploadAuthorImgMsg.includes("Failed")
                      ? "red"
                      : "green",
                  }}
                >
                  {uploadAuthorImgMsg}
                </p>
              )}
            </div>

            <div className="form_field">
              <label>Meta Title</label>
              <input
                type="text"
                name="metaTitle"
                value={formData.metaTitle}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Enter SEO title"
              />
            </div>

            <div className="form_field">
              <label>Meta Keywords</label>
              <input
                type="text"
                name="metaKeyword"
                value={formData.metaKeyword}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Enter meta keywords"
              />
            </div>

            <div className="form_field">
              <label>Meta Description</label>
              <textarea
                name="metaDescription"
                value={formData.metaDescription}
                onChange={handleInputChange}
                rows="3"
                cols="40"
                placeholder="Enter SEO meta description..."
                className="form-control"
              />
            </div>

            <div className="form_btn">
              <button type="submit" className="btn btn-primary">
                Save Blog
              </button>
              {status && (
                <div
                  style={{
                    marginTop: "10px",
                    color: isError ? "red" : "green",
                  }}
                >
                  {status}
                </div>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="category_list">
        <h2>Category List</h2>
        <div className="table_main">
          <table
            className="table table-bordered"
            style={{
              width: "100%",
              textAlign: "center",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr
                style={{ backgroundColor: "#f2f2f2", border: "1px solid #ddd" }}
              >
                <th>ID</th>
                <th>Blog Name</th>
                <th>Blog Description</th>
                <th>URl</th>
                <th>Blog categoryType</th>
                <th>Blog authorName</th>
                <th>Blog Image</th>
                <th>Blog Auther Image</th>
                <th>Blog Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {blogData &&
                blogData.map((blog, index) => (
                  <tr
                    key={index}
                    style={{
                      border: "1px solid #ddd",
                      textAlign: "center",
                    }}
                  >
                    <td>{index + 1}</td>
                    <td>{blog.title}</td>

                    <td
                      style={{
                        maxWidth: "200px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {blog.description}
                    </td>
                    <td>{blog.url_key}</td>
                    <td>{blog.categoryType}</td>
                    <td>{blog.authorName}</td>
                    <td>{blog.imagePath}</td>
                    <td>{blog.authorImagePath}</td>
                    <td
                      style={{
                        color: blog.blogStatus ? "#06892B" : "#FF0000",
                        fontWeight: "bold",
                      }}
                    >
                      {blog.blogStatus ? "Published" : "Unpublished"}
                    </td>
                    <td>
                      <button
                        onClick={() => setEditBlogId(blog.id)}
                        className="edit"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => openDeleteModal(blog.id)}
                        className="delete"
                      >
                        Delete
                      </button>
                    </td>
                    {/* <td>Add action buttons here if needed</td> */}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Blog edit modal */}
      {editBlogId && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <EditBlog
            blogId={editBlogId}
            onClose={() => setEditBlogId(null)}
            onUpdated={handleGetBlog}
          />
        </div>
      )}

      {/* blog edit model */}
      {isDelete && (
        <div className="delete-modal">
          <div className="delete-modal-content">
            <h2>Are you sure you want to delete this blog?</h2>
            <div className="delete-modal-buttons">
              <button className="delete-btn" onClick={confirmDelete}>
                Delete
              </button>
              <button className="cancel-btn" onClick={cancelDelete}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddBlog;
